import React, { useState, useRef } from "react";
import { Card, CardContent, Typography, Button, Box, Alert } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import axiosClient from "../axiosClient";
import { useStateContext } from "../context/ContextProvider";
import { useTranslation } from "react-i18next";

const ResetPasswordForm = () => {
  const { user } = useStateContext();

  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const oldpassRef = useRef(null);
  const passwordRef = useRef(null);
  const repasswordRef = useRef(null);
  const { t } = useTranslation();

  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long.";
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return "Password must contain at least 1 special character.";
    }
    return "";
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    // Reset previous messages
    setError("");
    setMessage("");

    // Validation
    if (
      !oldpassRef.current.value ||
      !passwordRef.current.value ||
      !repasswordRef.current.value
    ) {
      setError("All fields are required.");
      return;
    }

    if (passwordRef.current.value !== repasswordRef.current.value) {
      setError("Passwords do not match.");
      return;
    }

    // Validate new password requirements
    const passwordError = validatePassword(passwordRef.current.value);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    const payload = {
      username: user.username,
      old_password: oldpassRef.current.value,
      new_password1: passwordRef.current.value,
      new_password2: repasswordRef.current.value,
    };

    axiosClient
      .patch("auth/password_reset/", payload)
      .then(() => {
        setMessage("Password changed successfully");
        oldpassRef.current.value = "";
        passwordRef.current.value = "";
        repasswordRef.current.value = "";
      })
      .catch((err) => {
        if (err.response && err.response.data) {
          const errorMessage =
            err.response.data.new_password1 ||
            err.response.data.old_password ||
            err.response.data.non_field_errors ||
            "An unexpected error occurred.";
          setError(errorMessage);
        } else {
          setError("Network error. Please try again later.");
        }
        console.log("Error resetting password:", err);
      });
  };

  const handleClear = () => {
    // Clear input fields
    oldpassRef.current.value = "";
    passwordRef.current.value = "";
    repasswordRef.current.value = "";
    // Clear error and message states
    setError("");
    setMessage("");
  };

  return (
    <Card elevation={16}>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {t("Reset Password")}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {message}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Box sx={{ "& .MuiFormControl-root": { marginBottom: "1rem" } }}>
            <FormControl sx={{ width: "100%" }} variant="outlined">
              <InputLabel htmlFor="old-password">{t("Old Password")}</InputLabel>
              <OutlinedInput
                id="old-password"
                type={showPassword ? "text" : "password"}
                inputRef={oldpassRef}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label={t("Old Password")}
                required
              />
            </FormControl>

            <FormControl sx={{ width: "100%" }} variant="outlined">
              <InputLabel htmlFor="new-password">{t("New Password")}</InputLabel>
              <OutlinedInput
                id="new-password"
                type={showPassword ? "text" : "password"}
                inputRef={passwordRef}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label={t("New Password")}
                required
              />
            </FormControl>

            <FormControl sx={{ width: "100%" }} variant="outlined">
              <InputLabel htmlFor="confirm-password">
                {t("Confirm Password")}
              </InputLabel>
              <OutlinedInput
                id="confirm-password"
                type={showPassword ? "text" : "password"}
                inputRef={repasswordRef}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                label={t("Confirm Password")}
                required
              />
            </FormControl>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Button type="submit" variant="contained" color="primary">
              {t("Reset Password")}
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={handleClear}
            >
              {t("Clear")}
            </Button>
          </Box>
        </form>
      </CardContent>
    </Card>
  );
};

export default ResetPasswordForm;